import { useSignalEffect } from "@preact/signals";
import { decodeFilterCollection, encodeFilterCollection, getPermanentFilters } from "../filter/serialization";
import * as LocalStorage from "../local-storage";
import useQueryParamState from "./use-query-param-state";
/**
 * Automatically handle synchronising a FilterCollection with a query parameter. Optionally allow
 * synchronising with local storage by passing a localStorageKey value.
 *
 * It returns a [value, setValue] tuple rather than a signal, because setValue has optional params
 * that give control over how the query parameter is updated.
 */
export default function useFilterQueryParam(queryParam, filterConfig, localStorageKey) {
    const encodeValueForQueryString = (filters) => {
        return JSON.stringify(encodeFilterCollection(filterConfig, filters));
    };
    const decodeStoredValue = (qsValue) => {
        // Check whether there are any filters in localStorage. If so, use them as the initial value.
        let storedFilters = (() => {
            if (localStorageKey) {
                const localStorageValue = LocalStorage.getItem(localStorageKey);
                if (localStorageValue) {
                    // Only include filters that exist in this dashboard's filter config
                    return Object.fromEntries(Object.entries(localStorageValue).filter(([key]) => key in filterConfig));
                }
            }
            return {};
        })();
        // Query string parameters take priority over local storage. However if any required filters
        // are missing from the query string, the local storage values are merged in.
        if (qsValue) {
            try {
                const parsed = JSON.parse(qsValue);
                if (typeof parsed === "object") {
                    const requiredFilters = getPermanentFilters(filterConfig);
                    // Since we have valid-looking filters in the query string, we can discard any
                    // local storage filters that are not required.
                    storedFilters = Object.fromEntries(Object.entries(storedFilters).filter(([key]) => key in requiredFilters));
                    for (const key in parsed) {
                        if (key in filterConfig) {
                            storedFilters[key] = parsed[key];
                        }
                    }
                }
            }
            catch (e) {
                // Invalid filters in query string
            }
        }
        // The filters that were in localStorage and the query string as encoded/serialized, so we
        // need to pass them to decodeFilterCollection to rebuild them into a full FilterCollection.
        return decodeFilterCollection(filterConfig, storedFilters);
    };
    // Use the encode & decode functions defined above to synchronise the filters in a query parameter.
    const [filters, setFilters] = useQueryParamState(queryParam, decodeStoredValue, encodeValueForQueryString);
    useSignalEffect(() => {
        // React to selected filter changes and store them in local storage if a key is provided.
        if (localStorageKey) {
            const newValue = encodeFilterCollection(filterConfig, filters.value);
            const existingValue = LocalStorage.getItem(localStorageKey);
            if (existingValue) {
                Object.keys(existingValue).forEach((key) => {
                    if (!(key in filterConfig)) {
                        // Find any stored filters that don't exist on this dashboard, and retain
                        // their stored values. We don't do this for filters that *do* exist on this
                        // dashboard to allow filters to be removed.
                        newValue[key] = existingValue[key];
                    }
                });
            }
            LocalStorage.setItem(localStorageKey, newValue);
        }
    });
    return [filters, setFilters];
}
